import React, { FC, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { ClipboardList, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { IConsultation } from '../../store/types';
import { getTime } from '../../utils/getTime';
import { navigateToJournalSession } from '../../pages/journal/journal-navigation';
import { JournalConsultationFormatIcon } from './journal-consultation-format-icon';

/** Подписи чипов и подписи вкладок в SessionDetail (должны совпадать с `reportTypes` там). */
const aiAssistants: { label: string; sessionReportLabel: string }[] = [
  { label: 'ИИ супервизия', sessionReportLabel: 'ИИ супервизия' },
  { label: 'Конспект', sessionReportLabel: 'Конспект' },
  { label: 'Домашнее задание', sessionReportLabel: 'Домашнее задание' },
  { label: 'Метафоры', sessionReportLabel: 'Метафоры' },
  { label: 'ИИ ICF коуч', sessionReportLabel: 'ИИ ICF коуч' },
  { label: 'Сказкотерапия', sessionReportLabel: 'Сказкотерапия' },
];

const reportChipClassName =
  'inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 whitespace-nowrap cursor-pointer';

export interface AllSessionsProps {
  consultations: IConsultation[];
  onOpenSessionDetail?: (consultationId: number, initialReportLabel: string) => void;
}

const truncate = (text: string, maxLen: number) => {
  const trimmed = text.trim();
  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen)}...`;
};

const getSessionTitle = (consultation: IConsultation) => {
  const first = consultation.result?.[0];

  const fromConclusion = first?.result?.trim();
  if (fromConclusion) return truncate(fromConclusion.split('\n')[0] ?? fromConclusion, 70);

  const fromDpdg = first?.dpdg?.comment?.trim();
  if (fromDpdg) return truncate(fromDpdg.split('\n')[0] ?? fromDpdg, 70);

  const fromQuestion = first?.questions?.[0]?.question?.trim();
  if (fromQuestion) return truncate(fromQuestion.split('\n')[0] ?? fromQuestion, 70);

  return 'Без названия';
};

const getSessionSummary = (consultation: IConsultation) => {
  const first = consultation.result?.[0];
  const candidate = first?.dpdg?.comment?.trim() || first?.result?.trim();
  if (!candidate) return null;
  return truncate(candidate.split('\n')[0] ?? candidate, 160);
};

export const AllSessions: FC<AllSessionsProps> = ({ consultations, onOpenSessionDetail }) => {
  const navigate = useNavigate();

  const openSession = useCallback(
    (consultation: IConsultation, initialTab: string) => {
      if (onOpenSessionDetail) {
        onOpenSessionDetail(consultation.id, initialTab);
        return;
      }
      navigateToJournalSession(navigate, consultation.id, consultation.client_entity, initialTab);
    },
    [navigate, onOpenSessionDetail],
  );

  const sessions = useMemo(() => {
    return consultations.map((consultation, index) => {
      const hasReports = true;
      const title = getSessionTitle(consultation);
      const summary = getSessionSummary(consultation);
      const isUntitled = title === 'Без названия';

      const dateLabel = `${dayjs(new Date(consultation.date)).format('DD.MM.YYYY')} ${getTime(
        new Date(consultation.datetime_start),
      )}`;

      return {
        id: consultation.id,
        consultation,
        orderNumber: consultation.consultation_number ?? index + 1,
        dateLabel,
        title: isUntitled ? 'Сессия' : title,
        rawTitle: title,
        summary,
        hasReports,
      };
    });
  }, [consultations]);

  if (sessions.length === 0) {
    return (
      <div className="rounded-2xl bg-white p-5 text-center text-sm text-gray-500 shadow-[0px_10px_30px_rgba(0,0,0,0.04)]">
        Для выбранного клиента ещё нет сессий
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <span className="text-amber-600 mt-0.5" aria-hidden>
          ⏰
        </span>
        <div>
          <p className="text-sm font-medium text-gray-800">Прошло время с последней сессии — нужен быстрый повтор?</p>
          <p className="text-xs text-gray-500 mt-0.5">Освежите память перед следующей встречей</p>
        </div>
      </div>

      {sessions.map((session) => (
        <div key={session.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-sm transition-shadow">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-start gap-4">
              <span className="w-5 shrink-0 font-comfortaa text-base font-bold leading-tight text-[#111026]">
                {session.orderNumber}
              </span>
              <JournalConsultationFormatIcon consultation={session.consultation} />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-400 mb-1">{session.dateLabel}</p>

                <button
                  type="button"
                  className="text-gray-900 font-semibold cursor-pointer hover:text-[#C41230] transition-colors text-left w-full truncate"
                  onClick={() => openSession(session.consultation, 'Инструменты')}
                  title={session.rawTitle}
                >
                  {session.title}
                </button>

                {session.summary && <p className="text-sm text-gray-500 mt-1">{session.summary}</p>}
              </div>
            </div>

            <button type="button" className="text-gray-400 hover:text-gray-600 p-1 cursor-pointer" aria-label="Меню сессии">
              •••
            </button>
          </div>

          {session.hasReports && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => openSession(session.consultation, 'Инструменты')}
                className={reportChipClassName}
              >
                <ClipboardList size={14} className="shrink-0 text-slate-500" strokeWidth={2} aria-hidden />
                Инструменты
              </button>

              <button
                type="button"
                onClick={() => openSession(session.consultation, 'Транскрипция')}
                className={reportChipClassName}
              >
                <ClipboardList size={14} className="shrink-0 text-slate-500" strokeWidth={2} aria-hidden />
                Транскрипция
              </button>

              {aiAssistants.map((assistant) => (
                <button
                  key={assistant.sessionReportLabel}
                  type="button"
                  onClick={() => openSession(session.consultation, assistant.sessionReportLabel)}
                  className={reportChipClassName}
                >
                  <ClipboardList size={14} className="shrink-0 text-slate-500" strokeWidth={2} aria-hidden />
                  {assistant.label}
                </button>
              ))}

              <button
                type="button"
                className={reportChipClassName}
                onClick={() => openSession(session.consultation, 'add')}
              >
                <Plus size={14} className="shrink-0 text-slate-500" strokeWidth={2} aria-hidden />
                Добавить отчёт
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
